import { Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { CurrencyDetail } from '@apple/features/currencies/components/CurrencyDetail';
import { useCurrencyManagement } from '@apple/features/currencies/hooks/useCurrencyManagement';
import type {
	CurrencyConversion,
	CurrencyConversionDeleteItem,
} from '@apple/features/currencies/models';
import { currencyQueries } from '@apple/features/currencies/queries';

export const Route = createFileRoute(
	'/_authed/_admin/manage/currencies_/$fromCurrency_/$toCurrency',
)({
	parseParams: ({ fromCurrency, toCurrency }) => ({
		fromCurrency: z.string().parse(fromCurrency),
		toCurrency: z.string().parse(toCurrency),
	}),
	beforeLoad: ({ params: { fromCurrency, toCurrency }, ...args }) => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CurrencyConversionManagement.Read',
		});

		return {
			currencyQueryOptions: currencyQueries.getConversion(fromCurrency, toCurrency),
		};
	},
	loader: ({ context: { queryClient, currencyQueryOptions } }) => {
		void queryClient.prefetchQuery(currencyQueryOptions);
	},
	wrapInSuspense: true,
	component: ManageCurrencyRoute,
});

function ManageCurrencyRoute() {
	const { t } = useTranslation('manage');
	const { currencyQueryOptions } = Route.useRouteContext();

	const currencyQuery = useSuspenseQuery(currencyQueryOptions);

	const { edit, remove } = useCurrencyManagement();

	const goBackToList = useBackOrNavigate();

	const handleEdit = useCallback(
		async (currencyConversion: CurrencyConversion) => {
			await edit.mutateAsync(currencyConversion, {
				onSuccess: async () => {
					await goBackToList('/manage/currencies');
				},
			});
		},
		[edit, goBackToList],
	);

	const handleRemove = useCallback(
		async (currencyConversion: CurrencyConversionDeleteItem) => {
			await remove.mutateAsync([currencyConversion], {
				onSuccess: async () => {
					await goBackToList('/manage/currencies');
				},
			});
		},
		[goBackToList, remove],
	);

	return (
		<DetailsLayout title={t('currencies.edit')}>
			<Stack>
				<CurrencyDetail
					currencyConversion={currencyQuery.data}
					isEdit
					loading={edit.isPending || remove.isPending}
					submitted={edit.isSuccess || remove.isSuccess}
					onSave={handleEdit}
					onDelete={handleRemove}
				/>
			</Stack>
		</DetailsLayout>
	);
}
